import { forwardRef, useEffect, useRef, useState, type ReactNode } from 'react';
import { C2Button } from '../c2';
import { api, type Link as LinkRecord } from '../../lib/api';
import { gtagOnce, track } from '../../lib/analytics';

interface CreateLinkModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editing: LinkRecord | null;
  /** All links in the workspace; needed to look up alias_of for alternate-slug edits. */
  allLinks: LinkRecord[];
}

interface SaveError {
  message: string;
}

function inputStyle() {
  return {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid var(--color-hairline-strong)',
    borderRadius: 14,
    fontSize: 14,
    color: 'var(--color-ink)',
    background: 'var(--color-surface)',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
  };
}

interface FieldProps {
  label: string;
  sublabel?: string;
  required?: boolean;
  children: ReactNode;
}

function Field({ label, sublabel, required, children }: FieldProps) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-1.5">
        <label className="text-[12.5px] font-semibold text-ink">
          {label}
          {required && <span className="ml-[3px]" style={{ color: 'var(--color-peach-deep)' }}>*</span>}
        </label>
        {sublabel && <span className="text-[11.5px] text-ink-muted">{sublabel}</span>}
      </div>
      {children}
    </div>
  );
}

interface SlugInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  dim?: boolean;
}

const SlugInput = forwardRef<HTMLInputElement, SlugInputProps>(function SlugInput(
  { value, onChange, placeholder, dim },
  ref,
) {
  return (
    <div
      className="flex items-stretch overflow-hidden"
      style={{
        border: '1px solid var(--color-hairline-strong)',
        borderRadius: 14,
        background: dim ? 'var(--color-bg-alt)' : 'var(--color-surface)',
      }}
    >
      <span
        className="flex items-center font-mono font-bold"
        style={{
          padding: '0 14px',
          background: dim ? 'var(--color-bg-alt)' : 'var(--accent-soft)',
          color: dim ? 'var(--color-ink)' : 'var(--accent-deep)',
          fontSize: 14,
          borderRight: dim ? '1px solid var(--color-hairline)' : 'none',
        }}
      >
        go/
      </span>
      <input
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        className="flex-1 border-0 outline-none bg-transparent font-mono font-semibold text-ink"
        style={{ padding: '10px 14px', fontSize: 14 }}
      />
    </div>
  );
});

function AltsHelp() {
  return (
    <span className="relative inline-flex group" style={{ marginLeft: 4 }}>
      <span
        aria-label="Help"
        className="inline-flex items-center justify-center cursor-help"
        style={{
          width: 15,
          height: 15,
          borderRadius: '50%',
          background: 'var(--color-bg-alt)',
          color: 'var(--color-ink-muted)',
          fontSize: 10,
          fontWeight: 700,
        }}
      >
        ?
      </span>
      <span
        role="tooltip"
        className="absolute opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        style={{
          bottom: '100%',
          left: '50%',
          transform: 'translate(-50%, -8px)',
          background: 'var(--color-ink)',
          color: '#fff',
          padding: '8px 10px',
          borderRadius: 8,
          fontSize: 11,
          fontWeight: 400,
          width: 240,
          lineHeight: 1.45,
          zIndex: 10,
        }}
      >
        Alternates are helpful go/ links that point to the same URL. (e.g. go/vacation, go/pto, and go/takevacation could all point to where you log your vacation time.)
      </span>
    </span>
  );
}

function suggestSlug(url: string): string {
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    const last = parts[parts.length - 1] || u.hostname.split('.')[0] || '';
    return last
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 20);
  } catch {
    return '';
  }
}

const MAX_ALTS = 3;

export default function CreateLinkModal({ open, onClose, onSaved, editing, allLinks }: CreateLinkModalProps) {
  const isEdit = !!editing;
  const [url, setUrl] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  // Fixed-length array of alternate slug inputs — empty string means unused row.
  const [alts, setAlts] = useState<string[]>(['', '', '']);
  const [altsOpen, setAltsOpen] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  const [error, setError] = useState<SaveError | null>(null);
  const [saving, setSaving] = useState(false);
  const slugRef = useRef<HTMLInputElement | null>(null);

  // Auto-suggest slug from URL (create mode only)
  useEffect(() => {
    if (isEdit || slugEdited || !url) return;
    setSlug(suggestSlug(url));
  }, [url, slugEdited, isEdit]);

  // Initialize fields when modal opens
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setUrl(editing.url);
      setSlug(editing.slug);
      setDescription(editing.description ?? '');
      // alternates = other links in the workspace whose alias_of points to this primary
      const primaryId = editing.alias_of ?? editing.id;
      const groupAlts = allLinks
        .filter(l => l.alias_of === primaryId && l.id !== editing.id)
        .map(l => l.slug);
      // If editing is an alternate, include the primary slug as one of the alternates the user might keep.
      if (editing.alias_of != null) {
        const primary = allLinks.find(l => l.id === primaryId);
        if (primary && !groupAlts.includes(primary.slug)) groupAlts.push(primary.slug);
      }
      const padded = [...groupAlts.slice(0, MAX_ALTS), '', '', ''].slice(0, MAX_ALTS);
      setAlts(padded);
      setAltsOpen(groupAlts.length > 0);
      setSlugEdited(true);
    } else {
      setUrl('');
      setSlug('');
      setDescription('');
      setAlts(['', '', '']);
      setAltsOpen(false);
      setSlugEdited(false);
    }
    setError(null);
    setSaving(false);
    setTimeout(() => slugRef.current?.focus(), 100);
  }, [open, editing, allLinks]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const canSave = url.trim().length > 0 && slug.trim().length > 0 && !saving;

  // Cleaned, deduped alternates ready to send to the API. Mirrors what the
  // extension does on submit.
  const cleanedAlts = (() => {
    const seen = new Set<string>([slug]);
    const out: string[] = [];
    for (const raw of alts) {
      const a = raw
        .trim()
        .toLowerCase()
        .replace(/^(go\/)+/i, '')
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      if (a && !seen.has(a)) {
        seen.add(a);
        out.push(a);
      }
    }
    return out;
  })();

  const setAltAt = (i: number, v: string) => {
    setAlts(alts.map((a, idx) => idx === i ? v.toLowerCase().replace(/[^a-z0-9-]/g, '-') : a));
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      let primaryId: number;
      if (editing) {
        // Update primary record's url + slug + description
        // Group cascade is handled server-side for url + description.
        await api.updateLink(editing.slug, {
          slug,
          url,
          description: description || undefined,
        });
        primaryId = editing.alias_of ?? editing.id;
      } else {
        const created = await api.createLink({
          slug,
          url,
          description: description || undefined,
        });
        primaryId = created.id;
        track('link_created', { slug });
        gtagOnce('tgl_link_created');
      }

      // Reconcile alternates: create any new ones, delete any removed.
      const existingGroupAlts = allLinks.filter(l => l.alias_of === primaryId && (!editing || l.id !== editing.id));
      const existingAltSlugs = new Set(existingGroupAlts.map(l => l.slug));
      const desiredAltSlugs = new Set(cleanedAlts);

      // Create new alternates
      for (const altSlug of cleanedAlts) {
        if (!existingAltSlugs.has(altSlug)) {
          try {
            await api.createLink({
              slug: altSlug,
              url,
              description: description || undefined,
              alias_of: primaryId,
            });
          } catch {
            // If a slug collides with an unrelated link, skip silently;
            // the user can fix in a follow-up. The primary save already succeeded.
          }
        }
      }
      // Delete removed alternates
      for (const existing of existingGroupAlts) {
        if (!desiredAltSlugs.has(existing.slug)) {
          try { await api.deleteLink(existing.slug); } catch { /* ignore */ }
        }
      }

      onSaved();
      onClose();
    } catch (err) {
      setError({ message: (err as Error).message || 'Save failed' });
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete go/${editing.slug}? This can't be undone.`)) return;
    setSaving(true);
    try {
      await api.deleteLink(editing.slug);
      onSaved();
      onClose();
    } catch (err) {
      setError({ message: (err as Error).message || 'Delete failed' });
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[100]"
      style={{ background: 'oklch(0.2 0.02 260 / 0.4)', padding: 20 }}
      onClick={onClose}
    >
      <div
        className="w-full bg-surface overflow-hidden"
        style={{
          maxWidth: 560,
          borderRadius: 28,
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--color-hairline)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: '22px 28px 18px', borderBottom: '1px solid var(--color-hairline)' }}>
          <div className="flex items-center justify-between">
            <div>
              <div
                className="font-display font-bold text-ink"
                style={{ fontSize: 22, letterSpacing: '-0.01em' }}
              >
                {isEdit ? 'Edit go/ link' : 'New go/ link'}
              </div>
              <div className="text-[13px] text-ink-soft mt-0.5">
                {isEdit ? 'Changes propagate immediately to all teammates.' : 'Available to your team the moment you save.'}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center bg-transparent border-0 cursor-pointer text-ink-muted"
              style={{ borderRadius: 8 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div style={{ padding: '20px 28px' }} className="flex flex-col gap-4">
          <Field label="Go/ Link" required>
            <SlugInput
              ref={slugRef}
              value={slug}
              onChange={v => { setSlug(v); setSlugEdited(true); }}
              placeholder="pick a memorable name"
            />
          </Field>

          <div>
            <button
              type="button"
              onClick={() => setAltsOpen(v => !v)}
              className="bg-transparent border-0 cursor-pointer inline-flex items-center"
              style={{
                gap: 6,
                padding: 0,
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--accent-deep)',
                fontFamily: 'inherit',
              }}
            >
              <span style={{ fontSize: 15, lineHeight: 1, fontWeight: 700 }}>{altsOpen ? '–' : '+'}</span>
              <span>{altsOpen ? 'Alternate go/ links' : 'Add alternate go/ links'}</span>
              <AltsHelp />
            </button>
            {altsOpen && (
              <div className="flex flex-col mt-2.5" style={{ gap: 8 }}>
                {alts.map((a, i) => (
                  <SlugInput
                    key={i}
                    value={a}
                    onChange={v => setAltAt(i, v)}
                    placeholder="pick a memorable alternate"
                    dim
                  />
                ))}
              </div>
            )}
          </div>

          <Field label="URL" required>
            <input
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://example.com"
              style={inputStyle()}
            />
          </Field>

          <Field label="Description" sublabel="optional">
            <input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What is this link for?"
              style={inputStyle()}
            />
          </Field>

          {error && (
            <p className="text-[13px] m-0" style={{ color: 'var(--color-peach-deep)' }}>
              {error.message}
            </p>
          )}
        </div>

        <div
          className="flex items-center justify-between bg-bg-alt"
          style={{ padding: '16px 28px', borderTop: '1px solid var(--color-hairline)', gap: 12 }}
        >
          {isEdit ? (
            <button
              onClick={handleDelete}
              disabled={saving}
              className="bg-transparent border-0 cursor-pointer flex items-center gap-1.5"
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--color-peach-deep)',
                padding: '6px 8px',
                borderRadius: 8,
                transition: 'background 120ms ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-peach)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
              </svg>
              Delete link
            </button>
          ) : (
            <div className="text-xs text-ink-muted flex items-center gap-1.5">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="13 2 13 9 20 9" />
                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
              </svg>
              <span>Preview: <span className="font-mono font-semibold text-ink">go/{slug || '…'}</span></span>
            </div>
          )}
          <div className="flex gap-2">
            <C2Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>Cancel</C2Button>
            <C2Button
              variant={canSave ? 'accent' : 'outline'}
              size="sm"
              onClick={handleSave}
              disabled={!canSave}
              style={!canSave ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save go/ link'}
            </C2Button>
          </div>
        </div>
      </div>
    </div>
  );
}
